export type HazardType = 'meteor' | 'bomb' | 'laser'

export type BombRainPhase = 'lobby' | 'countdown' | 'playing' | 'results'

export interface BombRainHazard {
  id: string
  type: HazardType
  x: number
  y: number
  vx: number
  vy: number
  w: number
  h: number
  rotation: number
}

export interface BombRainPlayer {
  playerKey: string
  displayName: string
  x: number
  y: number
  w: number
  h: number
  vx: number
  alive: boolean
  eliminatedAtMs: number | null
  color: string
  skin: number
  facing: 1 | -1
  animT: number
}

export interface BombRainStanding {
  playerKey: string
  displayName: string
  rank: number
  survivedMs: number
}

export interface BombRainState {
  gameType: 'bomb_rain'
  tick: number
  phase: BombRainPhase
  round: number
  roundStartedAt: number | null
  roundDurationMs: number
  countdownEndsAt: number | null
  arena: { width: number; height: number }
  hostPlayerKey: string | null
  players: BombRainPlayer[]
  hazards: BombRainHazard[]
  speedMultiplier: number
  speedTier: number
  elapsedMs: number
  winner: { playerKey: string; displayName: string } | null
  standings: BombRainStanding[]
  aliveCount: number
  hadMultipleAlive: boolean
}

export type CrowdCanvasPhase = 'lobby' | 'countdown' | 'playing' | 'results'

export interface CrowdCanvasPoint {
  x: number
  y: number
}

export interface CrowdCanvasStroke {
  id: string
  playerKey: string
  color: string
  width: number
  points: CrowdCanvasPoint[]
}

export interface CrowdCanvasPlayer {
  playerKey: string
  displayName: string
  color: string
  ink: number
  cooldownUntil: number
  strokeCount: number
  brush: number
}

export interface CrowdCanvasAwards {
  bestContrib: string | null
  funniest: string | null
  bestArtist: string | null
}

export interface CrowdCanvasState {
  gameType: 'crowd_canvas'
  tick: number
  phase: CrowdCanvasPhase
  round: number
  theme: string
  roundStartedAt: number | null
  roundDurationMs: number
  countdownEndsAt: number | null
  arena: { width: number; height: number }
  hostPlayerKey: string | null
  players: CrowdCanvasPlayer[]
  strokes: CrowdCanvasStroke[]
  elapsedMs: number
  finalStroke: boolean
  finalStrokeGranted: boolean
  totalStrokes: number
  artistCount: number
  awards: CrowdCanvasAwards
}

export type GameState = BombRainState | CrowdCanvasState | BossArenaState

export interface GameEntityDelta {
  upsert?: Array<Record<string, unknown>>
  remove?: string[]
}

export interface GameStateDelta {
  players?: GameEntityDelta
  hazards?: GameEntityDelta
  strokes?: GameEntityDelta
  projectiles?: GameEntityDelta
  patch?: Record<string, unknown>
}

export type GameServerEvent =
  | {
      type: 'state_snapshot'
      sessionId: number
      tick: number
      state: GameState
      serverTime: number
    }
  | {
      type: 'state_delta'
      sessionId: number
      tick: number
      delta: GameStateDelta
      serverTime: number
    }
  | { type: 'session_ended'; sessionId: number }

export function formatSurvivedMs(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function isBombRainState(state: unknown): state is BombRainState {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as BombRainState).gameType === 'bomb_rain'
  )
}

export function isCrowdCanvasState(state: unknown): state is CrowdCanvasState {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as CrowdCanvasState).gameType === 'crowd_canvas'
  )
}

export type BossArenaPhase = 'lobby' | 'countdown' | 'playing' | 'results'
export type BossArenaTeam = 'boss' | 'monster'

export interface BossArenaPlayer {
  playerKey: string
  displayName: string
  role: 'host' | 'player'
  team: BossArenaTeam
  x: number
  y: number
  radius: number
  color: string
  hp: number
  maxHp: number
  alive: boolean
  facingDx: number
  facingDy: number
  shieldUntil?: number
  shieldCooldownUntil?: number
}

export interface BossArenaProjectile {
  id: string
  ownerKey: string
  team: BossArenaTeam
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  damage: number
}

export interface BossArenaState {
  gameType: 'boss_arena'
  tick: number
  phase: BossArenaPhase
  round: number
  roundStartedAt: number | null
  countdownEndsAt: number | null
  arena: { width: number; height: number }
  hostPlayerKey: string | null
  bossMaxHp: number
  players: BossArenaPlayer[]
  projectiles: BossArenaProjectile[]
  winnerSide: BossArenaTeam | 'draw' | null
  winnerLabel: string | null
  aliveMonsters: number
  bossAlive: boolean
}

export function isBossArenaState(state: unknown): state is BossArenaState {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as BossArenaState).gameType === 'boss_arena'
  )
}
